// Shaping for the GPU fleet board. The units arrive as Compute's own
// struct with no json tags (api/admin.ts ComputeUnitRaw), and their states
// are the five constants in internal/compute/types.go: STARTING, READY,
// STOPPING, STOPPED, FAILED. Everything here is about turning that into
// something an operator can scan in one look: how many units are in each
// state, which are costing money without serving, and how long each has
// been that way.
import type { ComputeUnitError, ComputeUnitRaw } from "../api/admin";

/** State words that mean "this unit is live and can serve". */
const SERVING = new Set(["READY"]);
/** State words that mean "this unit is billing but not serving yet" --
 * the transition that costs money for nothing if it stalls. */
const TRANSITIONAL = new Set(["STARTING", "STOPPING"]);

export type Tone = "ok" | "busy" | "bad" | "other";

export function unitTone(state?: string): Tone {
  const s = (state ?? "").toUpperCase();
  if (SERVING.has(s)) return "ok";
  if (TRANSITIONAL.has(s)) return "busy";
  if (s === "FAILED") return "bad";
  return "other";
}

/** Class suffix for `.a-state--*` / `.a-cu--*`. Lowercased so a state we
 * have never seen still lands on a defined, dim class instead of a
 * half-styled element. */
export function stateClass(state?: string): string {
  return (state ?? "unknown").toLowerCase();
}

export interface StateTally {
  state: string;
  count: number;
  tone: Tone;
}

/** One row per state, ordered by how much an operator should care:
 * failures first, then units that are billing without serving, then
 * serving units, then the parked ones. */
export function tallyStates(units: ComputeUnitRaw[]): StateTally[] {
  const counts = new Map<string, number>();
  for (const u of units) {
    const state = (u.State ?? "UNKNOWN").toUpperCase();
    counts.set(state, (counts.get(state) ?? 0) + 1);
  }
  const rank = (t: Tone) => (t === "bad" ? 0 : t === "busy" ? 1 : t === "ok" ? 2 : 3);
  return Array.from(counts.entries())
    .map(([state, count]) => ({ state, count, tone: unitTone(state) }))
    .sort((a, b) => rank(a.tone) - rank(b.tone) || b.count - a.count || a.state.localeCompare(b.state));
}

/** Units that are live in the sense that matters for spend: anything that
 * is not STOPPED or FAILED is holding a provider resource. */
export function liveUnits(units: ComputeUnitRaw[]): ComputeUnitRaw[] {
  return units.filter((u) => {
    const s = (u.State ?? "").toUpperCase();
    return s !== "STOPPED" && s !== "FAILED";
  });
}

/** A failed unit's error as one line: the code an operator greps for, then
 * the sentence that explains it. `source` names the service that reported
 * it, which belongs in a tooltip rather than in the line. Compute sends
 * {source, code, message} (09-errors-and-retries.md), never a string, so
 * this is not a formatting nicety -- rendering the raw field is a React
 * crash, and losing the message would lose the only diagnosis there is. */
export function describeError(err: ComputeUnitError): string {
  const code = err.code ?? "FAILED";
  return err.message ? `${code} · ${err.message}` : code;
}

/** Human-readable one-liner for the `Hardware`/`Pricing` fields, which are
 * json.RawMessage on the wire (internal/compute/types.go) -- so they may
 * be a string, an object, or absent, and the board must render all three
 * without a crash. */
export function describeRaw(value: unknown): string {
  if (value === undefined || value === null) return "—";
  if (typeof value === "string") return value || "—";
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>).filter(([, v]) => v !== null && v !== undefined);
    if (entries.length === 0) return "—";
    return entries.map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : String(v)}`).join(" ");
  }
  return String(value);
}

/** Backends that hold no GPU: the LLM API unit is an in-process client of a
 * hosted model (Compute's llm_api backend), priced at 0 $/h. */
const NON_GPU_BACKENDS = new Set(["llm_api"]);

/** Whether a unit is a rented GPU (anything but the LLM API backend). */
export function isGpuUnit(u: ComputeUnitRaw): boolean {
  return !NON_GPU_BACKENDS.has(u.Backend ?? "");
}

/** Live GPU units, newest first. */
export function liveGpuUnits(units: ComputeUnitRaw[]): ComputeUnitRaw[] {
  return newestFirst(liveUnits(units).filter(isGpuUnit));
}

/** Units newest first by CreatedAt (Compute lists them oldest first). */
export function newestFirst(units: ComputeUnitRaw[]): ComputeUnitRaw[] {
  return units.slice().sort((a, b) => (b.CreatedAt ?? "").localeCompare(a.CreatedAt ?? ""));
}

/** Units that failed within the window (FailedAt, else CreatedAt). */
export function recentFailures(units: ComputeUnitRaw[], windowMs: number, now = Date.now()): ComputeUnitRaw[] {
  return units.filter((u) => {
    if ((u.State ?? "").toUpperCase() !== "FAILED") return false;
    const t = Date.parse(u.FailedAt ?? u.CreatedAt ?? "");
    return !Number.isNaN(t) && now - t <= windowMs;
  });
}

function pricePerHourMinor(u: ComputeUnitRaw): number {
  const p = u.Pricing as { price_per_hour_minor?: unknown } | null | undefined;
  return typeof p?.price_per_hour_minor === "number" ? p.price_per_hour_minor : 0;
}

export interface GpuTime {
  hours: number;
  /** hours × each unit's list price when it was created: an estimate of
   * what the time cost, not a bill (boot and idle included, discounts not) */
  listCostMinor: number;
  units: number;
}

/** GPU time held inside [now − windowMs, now]: each GPU unit from creation
 * to its stop (or failure, or now if still live), clipped to the window. */
export function gpuTime(units: ComputeUnitRaw[], windowMs: number, now = Date.now()): GpuTime {
  const from = now - windowMs;
  let ms = 0;
  let cost = 0;
  let n = 0;
  for (const u of units) {
    if (!isGpuUnit(u)) continue;
    const start = Date.parse(u.CreatedAt ?? "");
    if (Number.isNaN(start)) continue;
    const endRaw = Date.parse(u.StoppedAt ?? u.FailedAt ?? "");
    const end = Number.isNaN(endRaw) ? now : endRaw;
    const overlap = Math.min(end, now) - Math.max(start, from);
    if (overlap <= 0) continue;
    ms += overlap;
    cost += (overlap / 3_600_000) * pricePerHourMinor(u);
    n++;
  }
  return { hours: ms / 3_600_000, listCostMinor: Math.round(cost), units: n };
}

const WEEK_MS = 7 * 24 * 3_600_000;

/** Live units first, then the rest newest first; the history is limited to
 * the last week unless "all" is chosen (Compute keeps every unit forever). */
export function boardUnits(units: ComputeUnitRaw[], all: boolean, now = Date.now()): ComputeUnitRaw[] {
  const live = newestFirst(liveUnits(units));
  const liveIds = new Set(live.map((u) => u.ID));
  const rest = newestFirst(units).filter((u) => {
    if (liveIds.has(u.ID)) return false;
    if (all) return true;
    const t = Date.parse(u.StoppedAt ?? u.FailedAt ?? u.CreatedAt ?? "");
    return !Number.isNaN(t) && now - t <= WEEK_MS;
  });
  return [...live, ...rest];
}
